import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import coverAsset from "@/assets/decorum_book_cover.png.asset.json";
import castleImg from "@/assets/join-castle.jpg";
import pillarsImg from "@/assets/join-four-pillars.jpg";
import lanceAsset from "@/assets/lance-ehlers.jpg.asset.json";
import { trackEvent } from "@/lib/analytics";
import { SALES_OPEN } from "@/lib/sales";

const STEPS = [
  { n: "I", title: "Read the book", body: SALES_OPEN ? "Your hardcover arrives in the mail. Each chapter lays the foundation for the training ahead." : "Read the hardcover when it releases. Each chapter lays the foundation for the training ahead." },
  {
    n: "II",
    title: "Scan the chapter codes",
    body: "Every chapter ends with a code. Scan it with your phone and it opens that chapter's reflection and assessment inside your Kingdom Hub. Your answers build your profile, record your reading progress, and unlock the next step of your training.",
  },
  { n: "III", title: "Train in the Kingdom Hub", body: "Daily orders, fitness and money check-ins, the Simulation, and a community of disciples put each chapter into practice." },
  { n: "IV", title: "Grow your avatar and estate", body: "Real progress in your life levels up your avatar and builds your estate, from ruins to a kingdom." },
];


const JoinButton = ({ location }: { location: string }) => (
  <div className="flex flex-col items-center gap-2">
    <Link
      to="/get-started"
      onClick={() => trackEvent("cta_click", { location, label: "join", destination: "/get-started" })}
      className="inline-flex items-center gap-3 rounded-full px-12 h-14 font-display text-2xl tracking-[0.15em] text-primary-foreground shadow-lg transition-transform hover:scale-[1.02]"
      style={{ background: "linear-gradient(180deg, hsl(38 55% 50%), hsl(34 55% 32%))" }}
    >
      {SALES_OPEN ? "JOIN" : "JOIN THE RELEASE LIST"} <ArrowRight className="h-5 w-5" />
    </Link>
    <p className="text-sm text-muted-foreground">
      {SALES_OPEN ? <><s className="opacity-70">$200</s> <span className="font-semibold text-gold-dark">$49.99</span> for a limited time</> : "The book is releasing soon. Be first to know."}
    </p>
  </div>
);

const Join = () => (
  <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
    <Header />

    {/* Hero */}
    <section className="relative">
      <div className="grid lg:grid-cols-2 items-stretch">
        <div className="px-6 sm:px-12 lg:px-16 py-12 lg:py-20 flex flex-col justify-center order-2 lg:order-1">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-gold-dark mb-8">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <h1 className="font-display text-5xl sm:text-6xl leading-[1.05] text-foreground">Join Decorum Lifestyle</h1>
          <div className="h-px w-24 bg-gold/70 my-6" />
          <p className="font-display text-xl sm:text-2xl text-foreground/80 max-w-lg">
            A book that begins your training, and a Kingdom Hub that keeps it going.
          </p>
          <div className="mt-10"><div className="inline-block"><JoinButton location="join_hero" /></div></div>
        </div>
        <div className="relative flex items-center justify-center py-12 lg:py-16 order-1 lg:order-2">
          <img src={coverAsset.url} alt="Decorum Lifestyle book cover by Lance Ehlers" width={1024} height={1536} className="w-56 sm:w-72 lg:w-96 h-auto rounded-sm shadow-2xl" />
        </div>
      </div>
    </section>

    {/* What you receive */}
    <section className="px-6 sm:px-12 lg:px-16 py-16">
      <h2 className="font-display text-3xl sm:text-4xl text-center tracking-[0.08em] uppercase">What you receive</h2>
      <p className="text-center text-muted-foreground mt-2 tracking-[0.15em] uppercase text-xs">One program. Everything included.</p>
      <div className="grid md:grid-cols-2 gap-6 mt-10 max-w-5xl mx-auto">
        {[
          { img: coverAsset.url, contain: true, title: "The Hardcover Book", body: "Every chapter connects to your training in the Kingdom Hub." },
          { img: castleImg, title: "Kingdom Hub", body: "Your training ground: daily orders, check-ins, the Simulation, your avatar and estate, and the community of disciples." },
        ].map((c) => (
          <div key={c.title} className="flex gap-5 rounded-xl border border-gold/40 bg-card/70 p-5 shadow-sm">
            <img src={c.img} alt="" loading="lazy" className={`w-32 sm:w-40 h-44 sm:h-52 rounded-md shrink-0 ${(c as any).contain ? "object-contain" : "object-cover"}`} />
            <div className="flex flex-col justify-center">
              <h3 className="font-display text-2xl">{c.title}</h3>
              <p className="text-muted-foreground mt-2 leading-relaxed">{c.body}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="max-w-5xl mx-auto mt-6 rounded-xl border border-gold/40 bg-card/70 p-6 sm:p-8 shadow-sm flex flex-col sm:flex-row gap-6 items-center sm:items-start">
        <div className="shrink-0 w-32 h-32 sm:w-40 sm:h-40 rounded-full border-2 border-gold/60 bg-background/60 flex items-center justify-center overflow-hidden">
          <img src={lanceAsset.url} alt="Lance Ehlers, coach and founder of Decorum Lifestyle" loading="lazy" className="w-full h-full object-cover object-top" />
        </div>
        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-gold-dark">Your coach</p>
          <h3 className="font-display text-2xl sm:text-3xl mt-1">Coaching from Lance Ehlers</h3>
          <p className="text-sm text-muted-foreground italic">Coach of the program · Founder and designer of Decorum Lifestyle</p>
          <div className="text-muted-foreground mt-4 space-y-3 leading-relaxed">
            <p>Lance Ehlers is the author and founder of Decorum Lifestyle. He graduated from Liberty University with a Bachelor of Science in Psychology, concentrating in Life Coaching. His studies in behavioral science and psychological theory helped shape his distinctive approach to personal development, integrating evidence-based psychological principles with biblical teachings. He is currently pursuing a Master's degree in Industrial-Organizational Psychology, further expanding his understanding of human behavior, motivation, and personal transformation.</p>
            <p>Beyond his work in coaching and personal development, Lance is a mortgage loan officer licensed in more than 15 states. His experience in financial services has given him firsthand insight into how personal financial decisions influence an individual's opportunities, stability, and ability to navigate modern financial systems.</p>
            <p>Through Decorum Lifestyle, Lance brings together faith, psychology, and financial literacy to equip individuals with the knowledge, discipline, and practical tools necessary to pursue meaningful, lasting change.</p>
          </div>
        </div>
      </div>
      {SALES_OPEN && <div className="max-w-5xl mx-auto mt-6 rounded-xl border border-gold/50 bg-card/80 p-6 text-center">
        <p className="font-display text-2xl">
          The full program: <s className="text-muted-foreground">$200</s> <span className="text-gold-dark font-semibold">$49.99</span>
        </p>
        <p className="text-muted-foreground mt-1">Limited time.</p>
      </div>}
    </section>

    {/* How it works */}
    <section className="px-6 sm:px-12 lg:px-16 py-16 bg-card/40">
      <h2 className="font-display text-3xl sm:text-4xl text-center tracking-[0.08em] uppercase">How the program works</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-12 max-w-6xl mx-auto">
        {STEPS.map((s) => (
          <div key={s.n} className="text-center">
            <div
              className="mx-auto h-20 w-20 rounded-full grid place-items-center font-display text-3xl text-primary-foreground shadow-md border-2 border-gold/60"
              style={{ background: "radial-gradient(circle at 35% 30%, hsl(40 60% 60%), hsl(34 55% 32%))" }}
            >
              {s.n}
            </div>
            <h3 className="font-display text-xl uppercase tracking-wide mt-4">{s.title}</h3>
            <p className="text-muted-foreground mt-2 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
    </section>

    {/* Four pillars */}
    <section className="relative">
      <img src={pillarsImg} alt="Marble temple with four golden pillars: Kingdom Theology, Psychology, Decorum, Stewardship" loading="lazy" width={1600} height={912} className="w-full h-auto block" />
    </section>

    {/* Final CTA */}
    <section className="py-16 px-6 text-center">
      <JoinButton location="join_footer" />
    </section>
  </div>
);

export default Join;
